import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, MapPin, Building2, Filter, Stethoscope, FlaskConical, Wrench, Eye, Activity, AlertTriangle } from 'lucide-react';
import { completedProjectsData } from '../data/mock';

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Diagnostic', 'Surgical', 'Infrastructure', 'Laboratory', 'Ophthalmology', 'Emergency', 'ENT', 'General'];

  const filteredProjects = selectedCategory === 'All' 
    ? completedProjectsData 
    : completedProjectsData.filter(project => project.category === selectedCategory);

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'Diagnostic': return <Activity size={16} style={{ color: '#2563eb' }} />;
      case 'Surgical': return <Stethoscope size={16} style={{ color: '#dc2626' }} />;
      case 'Infrastructure': return <Building2 size={16} style={{ color: 'var(--brand-teal)' }} />;
      case 'Laboratory': return <FlaskConical size={16} style={{ color: '#9333ea' }} />;
      case 'Ophthalmology': return <Eye size={16} style={{ color: '#8b5cf6' }} />;
      case 'Emergency': return <AlertTriangle size={16} style={{ color: '#ea580c' }} />;
      case 'ENT': return <Wrench size={16} style={{ color: '#059669' }} />;
      default: return <CheckCircle2 size={16} style={{ color: 'var(--brand-orange)' }} />;
    }
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'Diagnostic': return '#2563eb';
      case 'Surgical': return '#dc2626';
      case 'Infrastructure': return 'var(--brand-teal)';
      case 'Laboratory': return '#9333ea';
      case 'Ophthalmology': return '#8b5cf6';
      case 'Emergency': return '#ea580c';
      case 'ENT': return '#059669';
      default: return 'var(--brand-orange)';
    }
  };

  // Stats
  const stats = {
    total: completedProjectsData.length,
    diagnostic: completedProjectsData.filter(p => p.category === 'Diagnostic').length,
    surgical: completedProjectsData.filter(p => p.category === 'Surgical').length,
    infrastructure: completedProjectsData.filter(p => p.category === 'Infrastructure').length,
    locations: [...new Set(completedProjectsData.map(p => p.location))].length
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h1 className="hero-large mb-6">
              <span style={{ color: 'var(--brand-teal)' }}>Completed</span>
              {' '}
              <span style={{ color: 'var(--brand-orange)' }}>Projects</span>
            </h1>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '900px', margin: '0 auto' }}>
              A track record of successful healthcare infrastructure projects delivered across Maharashtra for Government Medical Colleges, Municipal Corporations, and Healthcare Institutions.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-12">
            <div 
              style={{
                background: 'var(--bg-secondary)',
                padding: '24px',
                borderRadius: '8px',
                textAlign: 'center',
                border: '2px solid var(--brand-teal)'
              }}
            >
              <div className="hero-medium" style={{ color: 'var(--brand-teal)' }}>{stats.total}</div>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Total Projects</p>
            </div>
            <div 
              style={{
                background: 'var(--bg-secondary)',
                padding: '24px',
                borderRadius: '8px',
                textAlign: 'center',
                border: '1px solid var(--border-light)'
              }}
            >
              <div className="hero-medium" style={{ color: '#2563eb' }}>{stats.diagnostic}</div>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Diagnostic</p>
            </div>
            <div 
              style={{
                background: 'var(--bg-secondary)',
                padding: '24px',
                borderRadius: '8px',
                textAlign: 'center',
                border: '1px solid var(--border-light)'
              }}
            >
              <div className="hero-medium" style={{ color: '#dc2626' }}>{stats.surgical}</div>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Surgical</p>
            </div>
            <div 
              style={{
                background: 'var(--bg-secondary)',
                padding: '24px',
                borderRadius: '8px',
                textAlign: 'center',
                border: '1px solid var(--border-light)'
              }}
            >
              <div className="hero-medium" style={{ color: 'var(--brand-teal)' }}>{stats.infrastructure}</div>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Infrastructure</p>
            </div>
            <div 
              style={{
                background: 'var(--bg-secondary)',
                padding: '24px',
                borderRadius: '8px',
                textAlign: 'center',
                border: '1px solid var(--border-light)'
              }}
            >
              <div className="hero-medium" style={{ color: 'var(--brand-orange)' }}>{stats.locations}</div>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Locations</p>
            </div>
          </div>
        </div>
      </section>

      {/* Filter & Projects List */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          {/* Category Filter */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Filter size={20} style={{ color: 'var(--brand-teal)' }} />
              <span className="body-regular" style={{ color: 'var(--text-primary)', fontWeight: '600' }}>Filter by Category:</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className="project-filter-btn"
                  style={{
                    padding: '10px 20px',
                    borderRadius: '24px',
                    border: selectedCategory === category ? '2px solid var(--brand-teal)' : '1px solid var(--border-light)',
                    background: selectedCategory === category ? 'var(--brand-teal)' : 'var(--bg-primary)',
                    color: selectedCategory === category ? 'white' : 'var(--text-primary)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    fontFamily: "'Suisse Medium', sans-serif",
                    fontSize: '14px'
                  }}
                >
                  {category} {category !== 'All' && `(${completedProjectsData.filter(p => p.category === category).length})`}
                </button>
              ))}
            </div>
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project, index) => (
              <div 
                key={project.id}
                className="project-card"
                style={{
                  background: 'var(--bg-primary)',
                  padding: '24px',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Project Number Badge */}
                <div 
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'var(--bg-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: 'var(--text-secondary)'
                  }}
                >
                  {project.id}
                </div>

                {/* Category Badge */}
                <div 
                  className="flex items-center gap-2 mb-3"
                  style={{
                    display: 'inline-flex',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    background: `${getCategoryColor(project.category)}15`,
                    border: `1px solid ${getCategoryColor(project.category)}30`
                  }}
                >
                  {getCategoryIcon(project.category)}
                  <span 
                    className="body-small" 
                    style={{ 
                      color: getCategoryColor(project.category),
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontSize: '10px'
                    }}
                  >
                    {project.category}
                  </span>
                </div>

                {/* Product Name */}
                <h3 
                  className="heading-3 mb-3" 
                  style={{ 
                    color: 'var(--text-primary)',
                    paddingRight: '40px'
                  }}
                >
                  {project.product}
                </h3>

                {/* Institution */}
                <div className="flex items-start gap-2 mb-2">
                  <Building2 size={16} style={{ color: 'var(--brand-teal)', flexShrink: 0, marginTop: '2px' }} />
                  <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                    {project.institution}
                  </p>
                </div>

                {/* Location */}
                <div className="flex items-center gap-2">
                  <MapPin size={16} style={{ color: 'var(--brand-orange)', flexShrink: 0 }} />
                  <p className="body-small" style={{ color: 'var(--text-light)' }}>
                    {project.location}
                  </p>
                </div>

                {/* Completed Badge */}
                <div 
                  className="flex items-center gap-2 mt-4 pt-4"
                  style={{ borderTop: '1px solid var(--border-light)' }}
                >
                  <CheckCircle2 size={16} style={{ color: '#10b981' }} />
                  <span className="body-small" style={{ color: '#10b981', fontWeight: '600' }}>
                    Completed
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Results Count */}
          <div className="text-center mt-8">
            <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              Showing <strong>{filteredProjects.length}</strong> of <strong>{completedProjectsData.length}</strong> projects
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding cta-section-dark">
        <div className="container text-center">
          <h2 className="hero-medium mb-4 cta-heading-light">
            Ready to Start Your Healthcare Infrastructure Project?
          </h2>
          <p className="body-large mb-8 cta-text-light">
            Join our list of successful projects. Contact us for your healthcare infrastructure needs.
          </p>
          <Link 
            to="/contact" 
            className="btn-primary btn-light-on-dark"
          >
            Request Project Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Projects;
